import React, { useCallback, useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import _ from 'lodash';
import { useQueryClient, useMutation } from '@tanstack/react-query';

import SimpleTable, { SortDirection, createDefaultActions } from 'components/Common/SimpleTable/SimpleTable';
import Tl from 'localization/LocalizationKeys';
import useAPI from 'services/useAPI';
import { useTranslation } from 'localization/useTranslation';
import ContentPage from 'components/ContentView/ContentPage/ContentPage';
import useAlerts from 'utils/hooks/useAlerts';
import createMutationRequest from 'utils/createMutationRequest';
import { providerKeys, useProviders } from './queries';

import DeleteProvider from './DeleteProvider';

const DefaultSortBy = {
  path: 'name',
  direction: SortDirection.DESC
};

const columns = [
  {
    label: Tl.common.name,
    path: 'displayName',
    searchItem: true,
    isSortable: true
  },
  {
    label: 'type',
    path: 'type',
    isSortable: true
  }
];

const AdminProvidersList = ({ breadcrumbs }) => {
  const T = useTranslation();
  const history = useHistory();
  const api = useAPI();
  const [alerts, addSuccessAlert, addErrorAlert] = useAlerts();

  const [confirmDeleteItem, setConfirmDeleteItem] = useState(null);
  const queryClient = useQueryClient();

  const { providers: providersNew, isLoading } = useProviders();

  const onNewProvider = useCallback(() => {
    history.push('providers/new-provider');
  }, [history]);

  const onViewProvider = useCallback((provider) => {
    history.push(`providers/${provider.id}`);
  }, [history]);

  const cancelDelete = useCallback(() => setConfirmDeleteItem(null), []);
  const onDelete = useCallback(provider => setConfirmDeleteItem(provider), []);
  const actions = useMemo(() => createDefaultActions({ onDelete }), [onDelete]);
  const deleteProvider = useMutation(createMutationRequest(api.core.deleteProvider), {
    onMutate: (provider) => {
      const oldProviders = queryClient.getQueryData(providerKeys.providers());
      queryClient.setQueryData(providerKeys.providers(), (oldProviders) => {
        return _.reject(oldProviders, { id: provider.id });
      });
      return oldProviders;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(providerKeys.providers());
      addSuccessAlert(T.common.request.delete.success);
    },
    onError: (error, unusedProvider, context) => {
      console.error(error);
      queryClient.setQueryData(providerKeys.providers(), context);
      addErrorAlert(T.common.request.delete.failed);
    }
  });


  return (
    <ContentPage breadcrumb={breadcrumbs} help={'admin/providers/list'}>
      { alerts }
      <SimpleTable
        isLoading={isLoading}
        data={providersNew}
        onRowClick={onViewProvider}
        sortBy={DefaultSortBy}
        globalActions={[{ onClick: onNewProvider }]}
        actions={actions}
        columns={columns}
        usePaging
        compact
      />
      <DeleteProvider
        confirmDeleteItem={confirmDeleteItem}
        onDelete={deleteProvider.mutate}
        cancelDelete={cancelDelete}
      />
    </ContentPage>
  );
};

export default AdminProvidersList;
